/**
 * Список файлов-вложений в баллоне комментария Activity.
 * Картинки рендерятся как thumbnail-preview, остальные — ссылкой со скрепкой.
 * Клик в обоих случаях открывает файл в новой вкладке.
 */
import { Group, Text } from '@mantine/core';
import { IconPaperclip } from '@tabler/icons-react';
import { resolveFileUrl } from '@ayocore/platform/filemanager/client/fileUrl';
import type { ActivityFileAttachment } from '@ayocore/activity/common/types/activity-message';

function formatFileSize(bytes: number | null | undefined): string {
  if (!bytes || bytes <= 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function isImageAttachment(file: ActivityFileAttachment): boolean {
  return typeof file.mime_type === 'string' && file.mime_type.startsWith('image/');
}

function ImageAttachment({ file, href }: { file: ActivityFileAttachment; href: string }) {
  const name = file.original_name ?? 'изображение';
  return (
    <a
      href={href}
      target='_blank'
      rel='noopener noreferrer'
      title={name}
      style={{
        display: 'inline-block',
        borderRadius: 8,
        overflow: 'hidden',
        lineHeight: 0,
        maxWidth: 240,
        maxHeight: 240,
      }}
    >
      <img
        src={href}
        alt={name}
        loading='lazy'
        style={{
          display: 'block',
          maxWidth: 240,
          maxHeight: 240,
          objectFit: 'cover',
          cursor: 'zoom-in',
        }}
      />
    </a>
  );
}

function FileLinkAttachment({
  file,
  href,
}: {
  file: ActivityFileAttachment;
  href: string | null;
}) {
  const name = file.original_name ?? 'файл';
  const size = formatFileSize(file.file_size);
  const labelNode = (
    <Group gap={4} wrap='nowrap' style={{ minWidth: 0 }}>
      <IconPaperclip size={12} color='var(--mantine-color-gray-6)' />
      <Text
        size='xs'
        c='blue.7'
        style={{
          textDecoration: 'underline',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          maxWidth: 220,
        }}
        title={name}
      >
        {name}
      </Text>
      {size ? (
        <Text size='10px' c='dimmed'>
          {size}
        </Text>
      ) : null}
    </Group>
  );

  return href ? (
    <a href={href} target='_blank' rel='noopener noreferrer' style={{ textDecoration: 'none' }}>
      {labelNode}
    </a>
  ) : (
    <span>{labelNode}</span>
  );
}

export function ActivityChatMessageFiles({ files }: { files: ActivityFileAttachment[] }) {
  if (!files || files.length === 0) return null;

  return (
    <Group gap={6} mt={4} wrap='wrap' align='flex-start'>
      {files.map(file => {
        const href = file.public_url ? resolveFileUrl(file.public_url) : null;
        if (href && isImageAttachment(file)) {
          return <ImageAttachment key={file.id} file={file} href={href} />;
        }
        return <FileLinkAttachment key={file.id} file={file} href={href} />;
      })}
    </Group>
  );
}
